import { CommonModule, registerLocaleData } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import localeEnIn from '@angular/common/locales/en-IN';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Chart, registerables } from 'chart.js';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { ChartModule, UIChart } from 'primeng/chart';
import { PrimecustomModule } from 'src/app/shared/primeng.custom.module';
import {
  VEHICLE_DATA,
  VEHICLE_TYPES_MASTER,
  VehicleRecord,
} from './ev-savings.config';
import { FUEL_OPTIONS } from 'src/app/features/panorama/ownership-cost/ownership-cost.config';

import {
  calculateAdvancedSavings,
  getChartData,
  VehicleType as VT,
  ModelVariant,
  AdvancedInput,
} from 'src/app/core/components/login/ev-savings-dialog/ev-savings-dialog.util';

Chart.register(...registerables);
registerLocaleData(localeEnIn);

@Component({
  selector: 'app-ev-savings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimecustomModule,
    FormsModule,
    ChartModule,
  ],
  templateUrl: './ev-savings-dialog.component.html',
  styleUrls: ['./ev-savings-dialog.component.scss'],
})
export class EvSavingsDialogComponent implements OnInit {
  @ViewChild('monthlyChart') monthlyChart!: UIChart;
  @ViewChild('cumulativeChart') cumulativeChart!: UIChart;

  private readonly destroy$ = new Subject<void>();
  form!: FormGroup;
  svgPath: any = 'assets/img/common/';

  vehicleOptions = VEHICLE_TYPES_MASTER.map((v) => ({
    label: v.label,
    value: v.value,
  }));
  modelOptions: { label: string; value: VehicleRecord }[] = [];
  fuelTypeOptions = FUEL_OPTIONS;
  selectedRecord: VehicleRecord | null = null;
  defaultDailyKm = 200;
  monthlyChartData: any;
  monthlyChartOptions: any;
  cumulativeChartData: any;
  cumulativeChartOptions: any;
  monthlySavings = 0;
  yearlySavings = { one: 0, five: 0, ten: 0 };
  expandedChartId: 'monthly' | 'cumulative' | null = null;

  SEGMENT_TO_VEHICLE_TYPE: Record<string, VT> = {
    VC03: '3W',
    VC01: 'SCV',
    Truck: 'Truck',
    TR: 'Tractor',
  };

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      vehicleType: [null],
      model: [null],
      dailyKm: [this.defaultDailyKm],
      fuelType: ['Diesel', Validators.required],
      fuelCost: [90, [Validators.required, this.rangeValidator(40, 150)]],
    });

    this.initCharts();
    this.formSubsChange();
  }

  rangeValidator(min: number, max: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '') return null;
      const value = Number(control.value);
      return value < min || value > max ? { rangeError: true } : null;
    };
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
    ];
    const input = event.target as HTMLInputElement;

    if (allowedKeys.includes(event.key)) return;

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const nextValue =
      input.value.slice(0, start) + event.key + input.value.slice(end);

    if (event.key === '.') {
      if (input.value.includes('.') || start === 0) {
        event.preventDefault();
      }
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }

    const maxLength = 6;
    if (nextValue.length > maxLength) {
      event.preventDefault();
      return;
    }

    const decimalIndex = nextValue.indexOf('.');
    if (decimalIndex !== -1 && nextValue.length - decimalIndex - 1 > 2) {
      event.preventDefault();
    }
  }

  private isFuelCostValid(): boolean {
    return this.form.get('fuelCost')?.valid === true;
  }

  private zeroOutSavingsAndCharts() {
    this.monthlySavings = 0;
    this.yearlySavings = { one: 0, five: 0, ten: 0 };
    this.resetChartsToEmpty();
    this.monthlyChart?.chart?.update();
    this.cumulativeChart?.chart?.update();
  }

  private toModelVariant(r: VehicleRecord | null): ModelVariant | undefined {
    if (!r) return undefined;

    const range = this.firstNumber(
      (r as any).range,
      (r as any).trueRange,
      (r as any).rangeKm,
      (r as any).evRangeKm
    );

    const batteryCapacity = this.firstNumber(
      (r as any).batteryCapacity,
      (r as any).battery_kwh,
      (r as any).batteryKwh,
      (r as any).battery,
      (r as any).batteryCapacityKWh
    );

    // Tractor is hour-based; per spec force 0/0
    const shortName = this.getShortName();
    const rangeFinal = shortName === 'Tractor' ? 0 : range;
    const battFinal = shortName === 'Tractor' ? 0 : batteryCapacity;

    return {
      model: (r as any).model ?? '',
      variant: (r as any).variant ?? '',
      range: rangeFinal,
      batteryCapacity: battFinal,
    };
  }

  private getIceMileage(r: VehicleRecord | null) {
    if (!r) return undefined;
    const mileagePerUnit = this.firstNumber((r as any).iceMileagePerUnit);
    return mileagePerUnit;
  }

  private buildAdvancedInput(
    rec: VehicleRecord,
    dailyKm: number
  ): AdvancedInput {
    const shortName = this.getShortName();
    const fuelType = this.form.value.fuelType ?? 'Diesel';
    const fuelPrice = Number(this.form.value.fuelCost ?? 90);

    return {
      shortName: shortName as any,
      fuelType,
      fuelPrice,
      dailyDistance: shortName === 'Tractor' ? undefined : dailyKm,
      dailyHours: shortName === 'Tractor' ? dailyKm : undefined,
      modelVariant: this.toModelVariant(rec),
      iceMileageKmPerUnit: this.getIceMileage(rec),
    };
  }

  openFuelDialog() {
    const vehicleType = this.form.get('vehicleType')?.value;
    let defaultFuel = 'Diesel';
    const defaultCost = 90;

    if (vehicleType === 'VC03') {
      defaultFuel = 'CNG';
    }

    this.form.get('fuelType')?.setValue(defaultFuel);
    this.form.get('fuelCost')?.setValue(defaultCost);
  }

  private formSubsChange() {
    this.form
      .get('vehicleType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((seg: string | null) => {
        this.selectedRecord = null;
        this.form.patchValue({ model: null }, { emitEvent: false });

        const newDaily = seg === 'Tractor' ? 2 : 200;
        this.form.patchValue({ dailyKm: newDaily }, { emitEvent: false });

        if (seg === '3W') {
          this.form.patchValue({ fuelType: 'CNG' }, { emitEvent: true });
        } else {
          this.form.patchValue({ fuelType: 'Diesel' }, { emitEvent: true });
        }

        if (seg) {
          const rows = VEHICLE_DATA.filter((r) => r.segment === seg);
          this.modelOptions = rows.map((r) => ({
            label: `${r.model}${r.variant ? ' - ' + r.variant : ''}`,
            value: r,
          }));
        } else {
          this.modelOptions = [];
        }
        this.monthlySavings = 0;
        this.yearlySavings = {
          one: 0,
          five: 0,
          ten: 0,
        };
        this.clampDailyForType();
        this.resetChartsToEmpty();

        setTimeout(() => {
          this.form.get('dailyKm')?.setValue(newDaily, { emitEvent: true });
        }, 0);
      });

    this.form
      .get('model')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((rec: VehicleRecord | null) => {
        this.selectedRecord = rec ?? null;
        const vehicleType = this.form.value.vehicleType;
        const dailyKm = this.form.value.dailyKm ?? this.defaultDailyKm;

        if (vehicleType && rec) {
          this.updateCharts(rec, dailyKm);
        } else {
          this.resetChartsToEmpty();
        }
      });

    this.form
      .get('dailyKm')
      ?.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((dailyKm: number) => {
        const rec: VehicleRecord | null = this.form.value.model ?? null;
        const vehicleType = this.form.value.vehicleType;
        if (vehicleType && rec) {
          this.updateCharts(rec, dailyKm);
        }
      });

    this.form
      .get('fuelType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalcFromForm());

    this.form
      .get('fuelCost')
      ?.valueChanges.pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isFuelCostValid()) {
          this.zeroOutSavingsAndCharts();
          return;
        }
        const rec: VehicleRecord | null = this.form.value.model ?? null;
        if (rec)
          this.updateCharts(
            rec,
            this.form.value.dailyKm ?? this.defaultDailyKm
          );
      });

    this.form
      .get('fuelCost')
      ?.statusChanges.pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        const rec: VehicleRecord | null = this.form.value.model ?? null;
        if (status !== 'VALID') {
          this.zeroOutSavingsAndCharts();
        } else if (rec) {
          this.updateCharts(
            rec,
            this.form.value.dailyKm ?? this.defaultDailyKm
          );
        }
      });
  }

  private clampDailyForType() {
    const val = Number(this.form.value.dailyKm ?? this.defaultDailyKm);
    if (this.isTractor) {
      const next = Math.min(10, Math.max(1, Math.round(val || 5)));
      if (next !== val)
        this.form.patchValue({ dailyKm: next }, { emitEvent: true });
    } else {
      const next = Math.min(1000, Math.max(15, val || this.defaultDailyKm));
      if (next !== val)
        this.form.patchValue({ dailyKm: next }, { emitEvent: true });
    }
  }

  get isTractor(): boolean {
    return this.form?.value?.vehicleType === 'Tractor';
  }

  private recalcFromForm() {
    const rec: VehicleRecord | null = this.form.value.model ?? null;
    const vehicleType = this.form.value.vehicleType;
    const dailyKm = this.form.value.dailyKm ?? this.defaultDailyKm;
    if (vehicleType && rec) this.updateCharts(rec, dailyKm);
  }

  private normalize(s: any): string {
    return (s ?? '').toString().trim().toUpperCase();
  }

  private deriveShortName(
    rec: VehicleRecord | null,
    segValue: string | null
  ): '3W' | 'SCV' | 'Truck' | 'Tractor' | null {
    const a = this.normalize(rec?.segment);
    const b = this.normalize(segValue);
    const s = a || b;

    if (/(^|[^A-Z])3W([^A-Z]|$)|VC03|AUTO/.test(s)) return '3W';
    if (/SCV|VC01|EVIATOR/.test(s)) return 'SCV';
    if (/TRUCK|RHINO/.test(s)) return 'Truck';
    if (/TRACTOR|^TR$|E-?(\d+)/.test(s)) return 'Tractor';

    return null;
  }

  private getShortName(): '3W' | 'SCV' | 'Truck' | 'Tractor' | null {
    return this.deriveShortName(
      this.selectedRecord,
      this.form?.value?.vehicleType ?? null
    );
  }

  private firstNumber(...vals: any[]): number {
    for (const v of vals) {
      const n = Number(v);
      if (isFinite(n) && n > 0) return n;
    }
    return 0;
  }

  private initCharts() {
    const rupeeFormatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    });

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
          },
          onClick: (
            event: any,
            legendItem: { datasetIndex: any },
            legend: { chart: any }
          ) => {
            const chart = legend.chart;
            const datasetIndex = legendItem.datasetIndex!;
            chart.setDatasetVisibility(
              datasetIndex,
              !chart.isDatasetVisible(datasetIndex)
            );
            chart.update();
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (context: any) {
              const value = context.parsed.y;
              return rupeeFormatter.format(value);
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Distance (Km)' },
          grid: { display: true },
        },
        y: {
          title: { display: true, text: 'Cost (₹)' },
          grid: { display: true },
          ticks: {
            callback: function (value: number | string) {
              return rupeeFormatter.format(Number(value));
            },
          },
        },
      },
    };

    this.monthlyChartOptions = baseOptions;
    this.monthlyChartData = {
      labels: ['100', '500', '1000', '2000', '3000'],
      datasets: [
        {
          label: 'Diesel',
          data: [],
          borderColor: '#e74c3c',
          fill: false,
          tension: 0.2,
          backgroundColor: '#e74c3c',
          pointBackgroundColor: '#e74c3c',
          pointBorderColor: '#e74c3c',
        },
        {
          label: 'EV',
          data: [],
          borderColor: '#2ecc71',
          fill: false,
          tension: 0.2,
          backgroundColor: '#2ecc71',
          pointBackgroundColor: '#2ecc71',
          pointBorderColor: '#2ecc71',
        },
      ],
    };

    this.cumulativeChartOptions = {
      ...baseOptions,
      scales: {
        x: {
          title: { display: true, text: 'Time' },
          grid: { display: true },
        },
        y: {
          title: { display: true, text: 'Savings (₹)' },
          grid: { display: true },
          ticks: {
            callback: function (value: number | string) {
              return rupeeFormatter.format(Number(value));
            },
          },
        },
      },
    };
    this.cumulativeChartData = {
      labels: ['1 Month', '1 Year', '5 Years', '10 Years'],
      datasets: [
        {
          label: 'Savings',
          data: [],
          borderColor: '#2ecc71',
          fill: false,
          tension: 0.2,
          backgroundColor: '#2ecc71',
          pointBackgroundColor: '#2ecc71',
          pointBorderColor: '#2ecc71',
        },
      ],
    };
  }

  private resetChartsToEmpty() {
    this.monthlyChartData = {
      ...this.monthlyChartData,
      datasets: [
        { ...this.monthlyChartData.datasets[0], data: [] },
        { ...this.monthlyChartData.datasets[1], data: [] },
      ],
    };

    this.cumulativeChartData = {
      ...this.cumulativeChartData,
      datasets: [{ ...this.cumulativeChartData.datasets[0], data: [] }],
    };
  }

  private updateCharts(rec: VehicleRecord, dailyKm: number) {
    if (!this.isFuelCostValid()) {
      this.zeroOutSavingsAndCharts();
      return;
    }

    const shortName = this.getShortName();
    if (!shortName) {
      this.zeroOutSavingsAndCharts();
      return;
    }

    const xAxisLabel = this.isTractor ? 'Hour Meter (Hrs)' : 'Distance (Km)';

    this.monthlyChartOptions = {
      ...this.monthlyChartOptions,
      scales: {
        ...this.monthlyChartOptions.scales,
        x: {
          ...this.monthlyChartOptions.scales.x,
          title: { display: true, text: xAxisLabel },
        },
      },
    };

    const input = this.buildAdvancedInput(rec, dailyKm);

    if (shortName !== 'Tractor') {
      const mv = input.modelVariant;
      if (!mv || mv.range <= 0 || mv.batteryCapacity <= 0) {
        this.zeroOutSavingsAndCharts();
        return;
      }
    }

    const cc = getChartData('cost-comparison', input);

    const allCostValues = [
      ...cc.datasets[0].data.map((v: number) => Math.round(v)),
      ...cc.datasets[1].data.map((v: number) => Math.round(v)),
    ];
    const minCostValue = Math.min(...allCostValues);

    this.monthlyChartData = {
      ...this.monthlyChartData,
      labels: cc.labels,
      datasets: [
        {
          ...this.monthlyChartData.datasets[0],
          label: 'ICE Cost',
          data: cc.datasets[0].data.map((v: number) => Math.round(v)),
        },
        {
          ...this.monthlyChartData.datasets[1],
          label: 'EV Cost',
          data: cc.datasets[1].data.map((v: number) => Math.round(v)),
        },
      ],
    };

    const adv = calculateAdvancedSavings(input);

    this.monthlySavings = Math.round(Math.max(0, adv.monthlySavings));
    this.yearlySavings = {
      one: Math.round(Math.max(0, adv.annualSavings)),
      five: Math.round(Math.max(0, adv.annualSavings * 5)),
      ten: Math.round(Math.max(0, adv.tenYearSavings)),
    };

    const cum = getChartData('cumulative-savings', input);
    const cumulativeValues = cum.datasets[0].data.map((v: number) =>
      Math.round(v)
    );
    const minCumulativeValue = Math.min(...cumulativeValues);

    this.cumulativeChartData = {
      ...this.cumulativeChartData,
      labels: cum.labels,
      datasets: [
        {
          ...this.cumulativeChartData.datasets[0],
          data: cumulativeValues,
        },
      ],
    };

    const rupeeFormatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    });

    this.monthlyChartOptions = {
      ...this.monthlyChartOptions,
      clip: false,
      scales: {
        ...this.monthlyChartOptions.scales,
        y: {
          ...this.monthlyChartOptions.scales.y,
          min: minCostValue,
          ticks: {
            callback: (value: number | string) =>
              rupeeFormatter.format(Number(value)),
          },
        },
      },
    };

    this.cumulativeChartOptions = {
      ...this.cumulativeChartOptions,
      clip: false,
      scales: {
        ...this.cumulativeChartOptions.scales,
        y: {
          ...this.cumulativeChartOptions.scales.y,
          min: minCumulativeValue,
          ticks: {
            callback: (value: number | string) =>
              rupeeFormatter.format(Number(value)),
          },
        },
      },
    };

    this.monthlyChart?.chart?.update();
    this.cumulativeChart?.chart?.update();
  }

  toggleExpand(chartId: 'monthly' | 'cumulative') {
    if (this.expandedChartId === chartId) {
      this.expandedChartId = null;
    } else {
      this.expandedChartId = chartId;
    }
  }
}
